// src/modules/requerimiento/utils/plataformasModem.js
// Consulta DETALLADA de un módem en todas las plataformas (Flespi 1 y 2, Wialon, LeaseControl, TRS Rastreo, YPFB Nacional e Internacional): además del
// último reporte de cada una (lo que ya hace verificarReporte), trae cómo está cargado el equipo en ella (nombre, ident, grupo, usuario, placa, alias…).
// Son las mismas consultas del panel "Plataformas donde está reportando" del Centro de Equipos.
import {
  searchFlespiPrimary,
  searchFlespiSecondary,
  searchLeasecontrolUnits,
  searchWialonUnits,
} from "../../../services/modems";
import { buscarVehiculoGlobal } from "../../vehiculos/services/vehiculosService";
import {
  listarGrupos as listarGruposTrsRastreo,
  listarVehiculos as listarVehiculosTrsRastreo,
} from "../../trsrastreo/services/trsRastreoService";
import { DIAS_REPORTE_VIEJO, formatearFechaReporte, parsearFechaReporte } from "./reporteModem";

/** id de la tabla `plataforma` (cliente_servicio.web) -> clave de plataforma de esta consulta. */
export const CLAVE_POR_ID_PLATAFORMA = { 1: "flespi1", 2: "flespi2", 3: "wialon", 4: "leasecontrol", 5: "nacional", 6: "internacional", 7: "rastreoBo", 8: "rastreoBo" };

const valor = (v) => (v === null || v === undefined || v === "" ? undefined : v);
const fecha = (ms) => (ms ? formatearFechaReporte(ms) : undefined);
const listaNombres = (lista) => (Array.isArray(lista) && lista.length ? lista.map((x) => x.nm || x.id).join(", ") : undefined);
const encendido = (v) => (v === true ? "Encendida" : v === false ? "Apagada" : undefined);

// Los grupos de TRS Rastreo son los mismos para todos los equipos: se piden una vez por minuto y se comparten entre consultas.
let gruposTrs = { t: 0, promesa: null };
const getGruposTrs = () => {
  if (!gruposTrs.promesa || Date.now() - gruposTrs.t > 60000) {
    gruposTrs = { t: Date.now(), promesa: listarGruposTrsRastreo().then((r) => r?.data?.data || []).catch(() => []) };
  }
  return gruposTrs.promesa;
};

const filasYpfb = (item) => {
  const extras = item?.extras || {};
  const cliente = extras.vehAlias || extras.CLINOMBRE || `${extras.nombre_normal || ""} ${extras.descripcion_normal || ""}`.trim();
  const velocidad = extras.vehVelocidadU;
  return [
    { label: "Alias", value: valor(item?.alias) },
    { label: "Cliente", value: valor(cliente) },
    { label: "Cuadrilla", value: valor(extras.cuadrilla) },
    { label: "Velocidad", value: velocidad != null ? `${velocidad} km/h` : undefined },
    { label: "Punto cercano", value: valor(extras.vehPuntoCercanoU || extras.VEHPUNTOCERCANOu) },
  ];
};

/**
 * Devuelve { estado: "reporta" | "no_reporta" | "no_se_pudo", plataformas, ultimoMs, dias, desactualizado }.
 * Cada plataforma: { key, titulo, color, nombre, placa, alias, grupo, filas: [{label, value}], ultimoMs, dias, desactualizado }
 * (`nombre` es el nombre con que está cargado el equipo; `placa`/`alias`/`grupo` solo cuando la plataforma los tiene, para compararlos con lo registrado).
 */
export async function consultarPlataformasModem({ num_sim, emai } = {}) {
  const valores = [num_sim, emai].map((v) => String(v ?? "").trim()).filter(Boolean);
  if (!valores.length) return { estado: "no_se_pudo", plataformas: [] };

  const [f1, f2, wi, le, gl] = await Promise.allSettled([
    searchFlespiPrimary(valores),
    searchFlespiSecondary(valores),
    searchWialonUnits(valores),
    searchLeasecontrolUnits(valores),
    buscarVehiculoGlobal(valores[0], valores[1]),
  ]);
  if ([f1, f2, wi, le, gl].every((r) => r.status === "rejected")) return { estado: "no_se_pudo", plataformas: [] };

  const ok = (r) => (r.status === "fulfilled" ? r.value : null);
  const flespi1 = ok(f1)?.data?.[0];
  const flespi2 = ok(f2)?.data?.[0];
  const wialon = ok(wi)?.data?.data?.[0];
  const leasecontrol = ok(le)?.data?.data?.[0];
  const globales = ok(gl)?.data || [];
  const porFuente = (fuente) => (Array.isArray(globales) ? globales : []).find((v) => String(v?.fuente || "").toLowerCase() === fuente.toLowerCase());
  const rastreoBo = porFuente("rastreo_bo");
  const nacional = porFuente("YPFB nacional");
  const internacional = porFuente("YPFB internacional");

  // TRS Rastreo: grupo y usuarios no vienen en la búsqueda global; se completan solo si el equipo está ahí.
  let grupoTrs;
  let usuarioTrs;
  if (rastreoBo) {
    const [grupos, ...vehiculos] = await Promise.all([
      getGruposTrs(),
      ...valores.map((v) => listarVehiculosTrsRastreo({ buscar: v, pageSize: 5 }).then((r) => r?.data?.data || []).catch(() => [])),
    ]);
    const vehiculo = vehiculos.flat().find((v) => String(v.id) === String(rastreoBo.uid)) || vehiculos.flat()[0];
    const delVehiculo = grupos.filter((g) => (g.cuadrilla_relacion || []).some((r) => r.cuarel_activo && String(r.for_id) === String(rastreoBo.uid)));
    grupoTrs = delVehiculo.map((g) => g.cua_descripcion).filter(Boolean).join(", ") || undefined;
    usuarioTrs = (vehiculo?.usuarios_relacionados || [])
      .filter((r) => r.estado_relacion)
      .map((r) => r.usuario?.usu_username || r.usuario?.usu_nombre)
      .filter(Boolean)
      .join(", ") || undefined;
  }

  const candidatas = [];
  if (flespi1) {
    const ms = parsearFechaReporte(flespi1.telemetry?.timestamp);
    candidatas.push({
      key: "flespi1", titulo: "Flespi 1", color: "#1976d2", nombre: valor(flespi1.name), ultimoMs: ms,
      filas: [
        { label: "Ident", value: valor(flespi1.configuration?.ident) },
        { label: "Subcuenta", value: valor(flespi1.subcuenta) },
        { label: "Ignición", value: encendido(flespi1.telemetry?.["engine.ignition.status"]) },
        { label: "Velocidad", value: `${flespi1.telemetry?.position?.speed ?? flespi1.telemetry?.["position.speed"] ?? 0} km/h` },
        { label: "Último reporte", value: fecha(ms) },
      ],
    });
  }
  if (flespi2) {
    const ms = parsearFechaReporte(flespi2.telemetry?.["server.timestamp"]);
    candidatas.push({
      key: "flespi2", titulo: "Flespi 2", color: "#0288d1", nombre: valor(flespi2.name), ultimoMs: ms,
      filas: [
        { label: "Ident", value: valor(flespi2.configuration?.ident) },
        { label: "Subcuenta", value: valor(flespi2.subcuenta) },
        { label: "Ignición", value: encendido(flespi2.telemetry?.["engine.ignition.status"]) },
        { label: "Batería", value: flespi2.telemetry?.["battery.level"] != null ? `${flespi2.telemetry["battery.level"]}%` : undefined },
        { label: "Último reporte", value: fecha(ms) },
      ],
    });
  }
  if (wialon) {
    const ms = parsearFechaReporte(wialon.lmsg?.t);
    candidatas.push({
      key: "wialon", titulo: "Wialon", color: "#ed6c02", nombre: valor(wialon.nm), grupo: listaNombres(wialon.grupos), ultimoMs: ms,
      filas: [
        { label: "UID", value: valor(wialon.uid) },
        { label: "GSM", value: valor(wialon.lmsg?.p?.gsm) },
        { label: "Voltaje", value: wialon.lmsg?.p?.voltage != null ? `${wialon.lmsg.p.voltage} V` : undefined },
        { label: "Grupo", value: listaNombres(wialon.grupos) },
        { label: "Usuario", value: listaNombres(wialon.usuarios) },
        { label: "Último reporte", value: fecha(ms) },
      ],
    });
  }
  if (leasecontrol) {
    const ms = parsearFechaReporte(leasecontrol.lmsg?.t);
    candidatas.push({
      key: "leasecontrol", titulo: "LeaseControl", color: "#9c27b0", nombre: valor(leasecontrol.nm), grupo: listaNombres(leasecontrol.grupos), ultimoMs: ms,
      filas: [
        { label: "UID", value: valor(leasecontrol.uid) },
        { label: "GPS válido", value: leasecontrol.lmsg?.p?.gps_valid === true ? "Sí" : leasecontrol.lmsg?.p?.gps_valid === false ? "No" : undefined },
        { label: "Grupo", value: listaNombres(leasecontrol.grupos) },
        { label: "Usuario", value: listaNombres(leasecontrol.usuarios) },
        { label: "Último reporte", value: fecha(ms) },
      ],
    });
  }
  if (rastreoBo) {
    const ms = parsearFechaReporte(rastreoBo.fecha_ultimo_registro);
    candidatas.push({
      key: "rastreoBo", titulo: "TRS Rastreo", color: "#2e7d32", nombre: valor(rastreoBo.placa), placa: valor(rastreoBo.placa), alias: valor(rastreoBo.alias), grupo: grupoTrs, ultimoMs: ms,
      filas: [
        { label: "Placa", value: valor(rastreoBo.placa) },
        { label: "Alias", value: valor(rastreoBo.alias) },
        { label: "Módem", value: valor(rastreoBo.modem) },
        { label: "Grupo", value: grupoTrs },
        { label: "Usuario", value: usuarioTrs },
        { label: "Último reporte", value: fecha(ms) },
      ],
    });
  }
  [["nacional", "YPFB Nacional", "#c62828", nacional], ["internacional", "YPFB Internacional", "#ad1457", internacional]].forEach(([key, titulo, color, item]) => {
    if (!item) return;
    const ms = parsearFechaReporte(item.fecha_ultimo_registro);
    candidatas.push({
      key, titulo, color, nombre: valor(item.placa), placa: valor(item.placa), alias: valor(item.alias), ultimoMs: ms,
      filas: [{ label: "Placa", value: valor(item.placa) }, ...filasYpfb(item), { label: "Último reporte", value: fecha(ms) }],
    });
  });

  const ahora = Date.now();
  const plataformas = candidatas.map((p) => {
    const dias = p.ultimoMs ? Math.max(0, Math.floor((ahora - p.ultimoMs) / 86400000)) : null;
    return { ...p, filas: p.filas.filter((f) => f.value !== undefined), dias, desactualizado: dias == null || dias >= DIAS_REPORTE_VIEJO };
  });

  if (!plataformas.length) return { estado: "no_reporta", plataformas };
  const ultimoMs = Math.max(0, ...plataformas.map((p) => p.ultimoMs || 0)) || null;
  const dias = ultimoMs ? Math.max(0, Math.floor((ahora - ultimoMs) / 86400000)) : null;
  return { estado: "reporta", plataformas, ultimoMs, dias, desactualizado: (dias ?? 0) >= DIAS_REPORTE_VIEJO };
}
